import { hasGame, S } from '../core/game';
import { caseLive, type CaseId } from '../content/clsPlan';
import { registerPropDrawers } from './propRegistry';
import { OUTLINE, block, ellipse, rect, shadow, shade, type Ctx } from './paint';
import type { Decor, Rect } from '../world/mapdef';

/** Props of the classroom package: the lost & found box, the notice board, the yard ball, and "!" alerts. */

const CJK = '"PingFang TC", "Noto Sans TC", "Noto Sans CJK TC", "Microsoft JhengHei", sans-serif';

/** Which case each `cls_alert` decor variant belongs to. */
export const ALERT_CASES: readonly CaseId[] = ['late', 'homework', 'standing'];

function lostBox(c: Ctx, r: Rect): void {
  const { x, y, w, h } = r;
  const by = y + h;
  shadow(c, x + w / 2, by, w / 2 + 2, 3);
  // Things poking out of the top: a glove, a bottle, a ruler.
  rect(c, OUTLINE, x + 5, by - 23, 2.4, 9);
  rect(c, '#ffd34e', x + 5.6, by - 22.4, 1.2, 8);
  block(c, x + w - 10, by - 24, 5, 10, '#5aa8e8', '#8fc8f0', '#3a7ab8');
  rect(c, '#2f4a8a', x + w - 9.5, by - 25.5, 4, 2);
  ellipse(c, '#e8735a', x + w / 2, by - 15, 4.5, 3);
  ellipse(c, '#f09a84', x + w / 2 - 1, by - 16, 2, 1.2);
  // Cardboard box with folded flaps.
  block(c, x, by - 15, w, 15, '#c99a5e', '#e0b67a', '#9c7038');
  c.fillStyle = '#b5864c';
  c.beginPath();
  c.moveTo(x - 2, by - 15);
  c.lineTo(x + 4, by - 19);
  c.lineTo(x + 7, by - 15);
  c.fill();
  c.beginPath();
  c.moveTo(x + w + 2, by - 15);
  c.lineTo(x + w - 4, by - 19);
  c.lineTo(x + w - 7, by - 15);
  c.fill();
  // A hand-written label: 失物招領 (lost & found).
  block(c, x + 3, by - 11, w - 6, 7, '#fff6dc');
  c.font = `900 5px ${CJK}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillStyle = '#c0392b';
  c.fillText('失物招領', x + w / 2, by - 7.4);
}

function noticeBoard(c: Ctx, r: Rect, t: number): void {
  const { x, y, w, h } = r;
  const by = y + h;
  shadow(c, x + w / 2, by, w / 2, 3);
  rect(c, OUTLINE, x + 4, by - 12, 2.5, 12);
  rect(c, OUTLINE, x + w - 6.5, by - 12, 2.5, 12);
  // Green frame, cork inside.
  block(c, x - 1, by - 40, w + 2, 30, '#3f7f52', '#57a06a', '#2a5a38');
  rect(c, '#c8945c', x + 1.5, by - 37.5, w - 3, 25);
  for (let i = 0; i < 14; i++) rect(c, 'rgba(90,50,20,0.25)', x + 3 + ((i * 7) % (w - 6)), by - 35 + ((i * 5) % 21), 1, 1);
  // Papers pinned up: timetable, a drawing, the lunch menu, and today's highlight.
  const papers: [string, number, number, number, number][] = [
    ['#fdf7e3', 3, -36, 11, 13],
    ['#ffd0dc', 16, -35, 9, 10],
    ['#cfe8ff', 27, -36, 10, 12],
    ['#e2f5d0', 6, -21, 12, 8],
  ];
  for (const [col, px, py, pw, ph] of papers) {
    if (px + pw > w - 2) continue;
    block(c, x + px, by + py, pw, ph, col);
    for (let l = 3; l < ph - 2; l += 2.5) rect(c, 'rgba(60,40,30,0.35)', x + px + 2, by + py + l, pw - 4, 0.6);
  }
  // Pins.
  for (const [px, py] of [[8, -36], [20, -35], [32, -36], [12, -21]] as const) {
    if (px > w - 3) continue;
    ellipse(c, '#e2493b', x + px, by + py, 1.2, 1.2);
  }
  // A little bee poster that wiggles on contest days (drawn whenever the board is animated).
  const k = Math.sin(t * 3) * 0.6;
  if (w > 30) {
    block(c, x + w - 13, by - 23 + k, 10, 10, '#ffe27a');
    ellipse(c, '#2a1810', x + w - 8, by - 18 + k, 2.4, 2);
    rect(c, '#ffd34e', x + w - 9.4, by - 19 + k, 0.8, 2);
    rect(c, '#ffd34e', x + w - 7.6, by - 19 + k, 0.8, 2);
  }
}

function ball(c: Ctx, r: Rect, t: number): void {
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h - 3;
  const live = hasGame() && caseLive(S(), 'ball');
  // During the argument the ball rocks a little between the two kids.
  const dx = live ? Math.sin(t * 2.2) * 1.5 : 0;
  shadow(c, cx + dx, cy + 3, 4, 1.4);
  ellipse(c, OUTLINE, cx + dx, cy, 4.2, 4.2);
  ellipse(c, '#f4f1ea', cx + dx, cy, 3.5, 3.5);
  ellipse(c, '#2a1810', cx + dx, cy, 1.3, 1.2);
  rect(c, '#2a1810', cx + dx - 3, cy - 2.2, 1.2, 1.2);
  rect(c, '#2a1810', cx + dx + 1.8, cy - 2.4, 1.2, 1.2);
  rect(c, '#2a1810', cx + dx - 0.6, cy + 2, 1.2, 1.2);
  ellipse(c, 'rgba(255,255,255,0.7)', cx + dx - 1.4, cy - 1.6, 0.9, 0.6);
}

/** A bobbing "!" speech bubble above a kid who needs a friend. */
export function alertBubble(c: Ctx, x: number, y: number, t: number, ch = '!'): void {
  const bob = Math.sin(t * 4) * 1.5;
  const by = y + bob;
  c.fillStyle = 'rgba(28,18,12,0.35)';
  c.beginPath();
  c.roundRect(x - 6.5, by - 7.5 + 1, 13, 13, 4);
  c.fill();
  c.fillStyle = '#fff3c4';
  c.strokeStyle = shade('#ffb347', -0.3);
  c.lineWidth = 1;
  c.beginPath();
  c.roundRect(x - 6.5, by - 7.5, 13, 13, 4);
  c.fill();
  c.stroke();
  c.beginPath();
  c.moveTo(x - 2.5, by + 5.3);
  c.lineTo(x + 2.5, by + 5.3);
  c.lineTo(x, by + 9);
  c.closePath();
  c.fillStyle = '#fff3c4';
  c.fill();
  c.font = '900 10px system-ui, sans-serif';
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillStyle = '#e2493b';
  c.fillText(ch, x, by - 0.5);
}

function decor(c: Ctx, d: Decor, t: number): boolean {
  if (d.kind !== 'cls_alert') return false;
  const id = ALERT_CASES[d.variant ?? 0];
  if (id && hasGame() && caseLive(S(), id)) alertBubble(c, d.x + 13, d.y - 40, t);
  return true;
}

registerPropDrawers({
  objects: { cls_lostBox: lostBox, cls_notice: noticeBoard, cls_ball: ball },
  decor,
  animatedObjects: ['cls_ball', 'cls_notice'],
  animatedDecor: ['cls_alert'],
});
